import { CommonModule } from "@angular/common";
import { Component, OnInit, EventEmitter, Output, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule } from "@angular/material/menu";
import { Router, RouterModule } from "@angular/router";
import { TokenStorageService } from "../../core/services/token-storage.service";
import { AuthenticationService } from "../../core/services/authentication.service";
import { TranslateModule } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "./confirm-dialog/confirm-dialog.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatListModule } from "@angular/material/list";
import { MatBadgeModule } from "@angular/material/badge";
import { MatDividerModule } from "@angular/material/divider";
import { ApplicationDataService } from "../../core/services/application-data.service";
import { CsrCallService } from "../../core/services/csr-call.service";
import { MatTableModule } from "@angular/material/table";
import { BehaviorSubject, Observable } from "rxjs";
import { CsrTheme, CSR_THEMES, applyCsrTheme, restoreCsrTheme } from "../../feature/csr/csr-theme";

interface NotificationsResponse {
  notifications: Array<{
    id: number;
    title: string;
    notificationMessage: string;
    createdAt: string;
    read: boolean;
  }>;
}

@Component({
  selector: "app-header",
  standalone: true,
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule,
    MatSidenavModule,
    MatListModule,
    MatBadgeModule,
    MatDividerModule,
    MatTableModule
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleSidenav = new EventEmitter<void>();
  
  private notificationCountSubject = new BehaviorSubject<number>(0);
  public notificationCount$: Observable<number> = this.notificationCountSubject.asObservable();
  
  private notificationInterval: any;
  
  userName!: string;
  notifications: any[] = [];
  readonly themes = CSR_THEMES;
  theme: CsrTheme = 'navy';
  showThemePicker = false;

  constructor(
    private tokenStorage: TokenStorageService,
    private authService: AuthenticationService,
    private router: Router,
    private dialog: MatDialog,
    private appDataService: ApplicationDataService,
    private csrCall: CsrCallService,
  ) {}

  getNotificationCount(): number {
    return this.notificationCountSubject.value;
  }

  get badgeLabel(): string {
    const count = this.notificationCountSubject.value;
    return count > 99 ? '99+' : String(count);
  }

  get activeTheme() {
    return CSR_THEMES.find(t => t.id === this.theme)!;
  }

  ngOnInit() {
    this.userName = this.tokenStorage.getEmail();
    this.theme = restoreCsrTheme();
    this.loadNotifications();
    this.startNotificationPolling();
  }

  ngOnDestroy(): void {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
  }

  startNotificationPolling(): void {
    this.notificationInterval = setInterval(() => {
      this.loadNotifications();
    }, 30 * 1000);
  }

  stopNotificationPolling(): void {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
      this.notificationInterval = null;
    }
  }
 logout(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.csrCall.selectExtension('', '');
        this.authService.logout().subscribe({
          next: () => {
            this.tokenStorage.removeToken();
            this.router.navigate(['login']);
          },
          error: (e) => {
            console.error('Logout error:', e);
            this.tokenStorage.removeToken();
            this.router.navigate(['login']);
          },
        });
      }
    });
  }
 
  onToggleSidenav() {
    this.toggleSidenav.emit();
  }

  applyTheme(id: CsrTheme): void {
    this.theme = id;
    applyCsrTheme(id);
    this.showThemePicker = false;
  }
 
  get unreadCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }
loadNotifications(): void {
  this.appDataService.getUserNotifications().subscribe({
    next: (res: any) => {
     // console.log('res:', res); // <== Check this output

      const list = Array.isArray(res?.data) ? res.data : [];
      this.notifications = list.map((item: any) => ({
        id: item.id,
        title: 'System',
        message: item.notificationMessage || '',
        time: this.getTimeAgo(item.createdAt),
        read: item.read || false,
        complaintId: item.complaintId || item.complaint_id || item.id,
        createdAt: item.createdAt
      }));

       this.updateNotificationCount(); // <--- Add this
      this.sortNotifications();      
    },
    error: (err: any) => {
      console.error('API failed:', err);
    }
  });
}

  sortNotifications(): void {
    this.notifications.sort((a, b) => {
      // if (a.read !== b.read) {
      //   return a.read ? 1 : -1;
      // }
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }

  updateNotificationCount(): void {
    const unreadCount = this.notifications.filter(notification => !notification.read).length;
    this.notificationCountSubject.next(unreadCount);
  }

markAsRead(notification: any, event?: Event): void {
  if (event) {
    event.stopPropagation();
  }

  if (!notification.read) {
    // Update in place
    notification.read = true;
    this.updateNotificationCount();

    this.appDataService.markReadNotification(notification.id).subscribe({
      next: () => {
      //  console.log("Notification marked as read successfully");
        // Do nothing to avoid reordering
      },
      error: (err: any) => {
        console.error("Error marking as read:", err);
        // Revert only if error occurs
        notification.read = false;
        this.updateNotificationCount();
      }
    });
  }
}


  viewComplaint(notification: any): void {
    this.markAsRead(notification);
    this.router.navigate(["complaint/edit", notification.complaintId]);
  }

  getTimeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return `${diffInDays}d ago`;
    }
  }
  trackById(index: number, notification: any): any {
  return notification.id; // or whatever unique identifier you have
}

}
